import { compareVersionStrings } from './device'
import safeURLParse from './safeURLParse'

const FORCE_WEB_VIEW_QUERY_PARAM = 'fwv'
const FORCE_HANDOFF_QUERY_PARAM = 'force_safari'

const INSTAGRAM_USER_AGENT_PATTERN = /\bInstagram\b/
const TWITTER_UA = /\bTwitter\b/
// Branded iOS browsers can expose bridges and lack Safari's WebPageNamespace.
const THIRD_PARTY_BROWSER_USER_AGENT_PATTERN = /\b(?:CriOS|FxiOS|EdgiOS|OPiOS|OPT|YaBrowser|DuckDuckGo)\b/

export type BrowserHandoff = {
    method: 'location' | 'meta-refresh'
    url: string
}

interface InAppBrowserEnvironment {
    self: unknown
    top: unknown
    navigator: Pick<Navigator, 'maxTouchPoints' | 'userAgent'> & { standalone?: boolean }
    browser?: unknown
    matchMedia?: (query: string) => Pick<MediaQueryList, 'matches'>
    webkit?: {
        messageHandlers?: {
            playgamaApp?: unknown
        }
    }
}

interface HandoffEnvironment {
    location: Pick<Location, 'assign' | 'replace'>
    document: Pick<Document, 'createElement' | 'head'>
}

export function detectInAppBrowser(
    environment: InAppBrowserEnvironment,
    targetUrl: URL,
    force = false,
): BrowserHandoff | undefined {
    const { maxTouchPoints, userAgent } = environment.navigator
    const isIos = /(iPhone|iPad|iPod)/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)

    if (!isIos || environment.self !== environment.top) {
        return undefined
    }
    const safariVersion = /\bVersion\/(\d+(?:\.\d+)*)/.exec(userAgent)?.[1]
    if (safariVersion && /\bSafari\//.test(userAgent) && !compareVersionStrings(safariVersion, '15.4')) {
        return undefined
    }
    if (targetUrl.protocol !== 'http:' && targetUrl.protocol !== 'https:') {
        return undefined
    }
    if (targetUrl.searchParams.get(FORCE_WEB_VIEW_QUERY_PARAM) === '1') {
        return undefined
    }

    if (
        !force &&
        (THIRD_PARTY_BROWSER_USER_AGENT_PATTERN.test(userAgent) ||
            environment.navigator.standalone ||
            environment.matchMedia?.('(display-mode: standalone)').matches ||
            environment.webkit?.messageHandlers?.playgamaApp ||
            TWITTER_UA.test(userAgent))
    ) {
        return undefined
    }

    const isInstagram = INSTAGRAM_USER_AGENT_PATTERN.test(userAgent)
    // Safari namespace signal used by FingerprintJS's browser research:
    // https://github.com/fingerprintjs/fingerprintjs/blob/master/src/utils/browser.ts
    if (!force && !isInstagram && String(environment.browser) === '[object WebPageNamespace]') {
        return undefined
    }

    const url = safeURLParse(targetUrl.href)
    if (!url) {
        return undefined
    }
    url.searchParams.delete(FORCE_HANDOFF_QUERY_PARAM)
    url.searchParams.set(FORCE_WEB_VIEW_QUERY_PARAM, '1')

    if (isInstagram) {
        return {
            method: 'meta-refresh',
            url: `instagram://extbrowser/?url=${encodeURIComponent(url.href)}`,
        }
    }

    return {
        method: 'location',
        url: url.href.replace(/^https?:/, 'x-safari-https:'),
    }
}

export function requestBrowserHandoff(handoff: BrowserHandoff, environment: HandoffEnvironment = window): void {
    if (handoff.method === 'location') {
        environment.location.replace(handoff.url)
        return
    }

    const meta = environment.document.createElement('meta')
    meta.httpEquiv = 'refresh'
    meta.content = `0;url=${handoff.url}`
    environment.document.head.appendChild(meta)
}

export function openBrowserHandoff(
    handoff: BrowserHandoff,
    environment: Pick<HandoffEnvironment, 'location'> = window,
): void {
    environment.location.assign(handoff.url)
}
